// AXIS ZERO - Player Module
// VISUAL: Simple square with trail, hardcore minimal style
// Handles player physics, jumping, and rendering

import { CONFIG } from './Config';

export class Player {
  constructor(canvasHeight) {
    this.width = CONFIG.PLAYER_WIDTH;
    this.height = CONFIG.PLAYER_HEIGHT;
    this.x = 80;
    this.groundY = canvasHeight - CONFIG.PLAYER_GROUND_Y_OFFSET;
    this.y = this.groundY - this.height;
    
    // Physics state (DO NOT CHANGE)
    this.velocityY = 0;
    this.isGrounded = true;
    this.coyoteTimer = 0;
    this.jumpBufferTimer = 0;
    this.hasJumpBuffered = false;
    
    // Trail system
    this.trail = [];
    this.maxTrailLength = 4;
    this.trailSpawnTimer = 0;
    
    // Death state
    this.isDead = false;
    this.deathFlashAlpha = 0;
    
    // Glow/streak multiplier
    this.glowMultiplier = 1;
    
    // Landing particles
    this.landingParticles = [];
    this.wasInAir = false;
  }

  reset(canvasHeight) {
    this.groundY = canvasHeight - CONFIG.PLAYER_GROUND_Y_OFFSET;
    this.y = this.groundY - this.height;
    this.velocityY = 0;
    this.isGrounded = true;
    this.coyoteTimer = 0;
    this.jumpBufferTimer = 0;
    this.hasJumpBuffered = false;
    this.isDead = false;
    this.deathFlashAlpha = 0;
    this.glowMultiplier = 1;
    this.trail = [];
    this.landingParticles = [];
    this.wasInAir = false;
  }

  jump(isHighMode = false) {

    const canJump = this.isGrounded || this.coyoteTimer > 0;
    
    if (canJump) {
      this.velocityY = CONFIG.JUMP_VELOCITY * (isHighMode ? (CONFIG.HIGH_MODE_JUMP_MULT || 1) : 1);
      this.isGrounded = false;
      this.coyoteTimer = 0;
      this.jumpBufferTimer = 0;
      this.hasJumpBuffered = false;
      this.wasInAir = true;
      return true;
    } else {
      this.jumpBufferTimer = CONFIG.JUMP_BUFFER;
      this.hasJumpBuffered = true;
      return false;
    }
  }

  triggerNearMissBlink() {
    // Minimal - just increase glow briefly
    this.glowMultiplier = Math.min(this.glowMultiplier + 0.3, 2.5);
  }

  triggerDeath() {
    this.isDead = true;
    this.deathFlashAlpha = 1;
  }

  spawnLandingParticles() {
    const centerX = this.x + this.width / 2;
    const groundY = this.groundY;
    
    for (let i = 0; i < 5; i++) {
      const angle = Math.PI + (Math.random() - 0.5) * 0.8;
      const speed = 30 + Math.random() * 50;
      this.landingParticles.push({
        x: centerX + (Math.random() - 0.5) * this.width,
        y: groundY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 30,
        life: 0.3,
        maxLife: 0.3,
        size: 2 + Math.random() * 2,
      });
    }
  }

  setGlowMultiplier(mult) {
    this.glowMultiplier = mult;
  }

  update(deltaTime, timeScale = 1, worldColors = null) {
    const dt = deltaTime * timeScale;
    
    // Update death flash
    if (this.isDead) {
      this.deathFlashAlpha = Math.max(0, this.deathFlashAlpha - dt * 10);
      return;
    }
    
    // Apply gravity (DO NOT CHANGE physics)
    if (!this.isGrounded) {
      this.velocityY += CONFIG.GRAVITY * dt;
      this.y += this.velocityY * dt;
      
      if (this.coyoteTimer > 0) {
        this.coyoteTimer -= dt;
      }
    }
    
    // Ground collision (DO NOT CHANGE)
    if (this.y >= this.groundY - this.height) {
      this.y = this.groundY - this.height;
      
      // Landing effect
      if (this.wasInAir && this.velocityY > 0) {
        this.spawnLandingParticles();
        this.wasInAir = false;
      }
      
      this.velocityY = 0;
      this.isGrounded = true;
      
      if (this.hasJumpBuffered && this.jumpBufferTimer > 0) {
        this.jump();
      }
    } else if (this.isGrounded && this.velocityY !== 0) {
      this.isGrounded = false;
      this.coyoteTimer = CONFIG.COYOTE_TIME;
    }
    
    // Update jump buffer
    if (this.jumpBufferTimer > 0) {
      this.jumpBufferTimer -= dt;
      if (this.jumpBufferTimer <= 0) {
        this.hasJumpBuffered = false;
      }
    }
    
    // Update trail
    this.trailSpawnTimer -= dt;
    if (this.trailSpawnTimer <= 0) {
      // Trail length based on streak
      const trailLen = Math.floor(this.maxTrailLength * (0.5 + this.glowMultiplier * 0.25));
      
      this.trail.unshift({
        x: this.x,
        y: this.y,
        alpha: 1,
        size: this.width,
      });
      
      if (this.trail.length > trailLen) {
        this.trail.pop();
      }
      
      this.trailSpawnTimer = 0.02;
    }
    
    // Update trail alpha (fast fade, short-lived)
    this.trail.forEach((t, i) => {
      t.alpha = (1 - i / this.trail.length) * 0.35;
    });
    
    // Update landing particles
    this.landingParticles = this.landingParticles.filter(p => {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 200 * dt;
      p.life -= dt;
      return p.life > 0;
    });
    
    // Smooth glow decay
    if (this.glowMultiplier > 1) {
      this.glowMultiplier = Math.max(1, this.glowMultiplier - dt * 0.5);
    }
  }

  draw(ctx, worldColors = null) {
    const colors = worldColors || CONFIG.COLORS;
    
    ctx.save();
    
    // Draw trail (behind player, no glow - glow only on main square)
    this.trail.forEach((t, i) => {
      if (t.alpha > 0.05) {
        ctx.globalAlpha = t.alpha;
        ctx.fillStyle = colors.PLAYER_TRAIL || colors.PLAYER;
        ctx.fillRect(t.x, t.y, t.size, this.height);
      }
    });
    
    ctx.globalAlpha = 1;
    
    // Draw death flash
    if (this.deathFlashAlpha > 0) {
      ctx.fillStyle = CONFIG.COLORS.DEATH_FLASH;
      ctx.globalAlpha = this.deathFlashAlpha;
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.globalAlpha = 1;
    }
    
    // Draw player (simple square with glow)
    ctx.shadowColor = colors.PLAYER_GLOW;
    ctx.shadowBlur = 15 * this.glowMultiplier;
    ctx.fillStyle = colors.PLAYER;
    ctx.fillRect(this.x, this.y, this.width, this.height);
    
    // Draw landing particles
    if (this.landingParticles.length > 0) {
      this.landingParticles.forEach(p => {
        ctx.globalAlpha = p.life / p.maxLife;
        ctx.fillStyle = colors.PLAYER;
        ctx.shadowBlur = 5;
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
      });
    }
    
    ctx.restore();
  }

  getBounds() {
    // Hitbox (DO NOT CHANGE)
    return {
      x: this.x + 5,
      y: this.y + 5,
      width: this.width - 10,
      height: this.height - 5,
    };
  }

  updateCanvasHeight(canvasHeight) {
    const wasGrounded = this.isGrounded;
    const distFromGround = this.groundY - this.height - this.y;
    
    this.groundY = canvasHeight - CONFIG.PLAYER_GROUND_Y_OFFSET;
    
    if (wasGrounded) {
      this.y = this.groundY - this.height;
    } else {
      this.y = this.groundY - this.height - distFromGround;
    }
  }
}

export default Player;
